import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import Navbar from '../../components/Navbar';
import { ChevronLeft, AlertTriangle, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const INCIDENT_TYPES = [
  { value: 'safety_concern', label: 'Safety Concern' },
  { value: 'criminal_charge', label: 'Criminal Charge' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'policy_violation', label: 'Policy Violation' },
  { value: 'other', label: 'Other' }
];

const IncidentReportForm = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    volunteer_id: '',
    incident_type: 'safety_concern',
    date: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.volunteer_id.trim()) {
      toast.error('Please enter the volunteer email');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please provide a description of the incident');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/incidents`, formData);
      toast.success('Incident report submitted. The volunteer\'s clearance is now under review.');
      navigate('/partner/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit incident report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="incident-report-form">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/partner/dashboard" className="text-[#8B2E3C] hover:underline flex items-center gap-1 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back to Partner Dashboard
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-2">Report Incident</h1>
          <p className="text-base text-[#666666]">Submit a confidential incident report about a volunteer</p>
        </div>

        {/* Warning Banner */}
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-orange-600 shrink-0" />
            <div>
              <h3 className="font-bold text-orange-800">Important Notice</h3>
              <p className="text-sm text-orange-700 mt-1">
                This form is for reporting serious concerns about volunteer conduct. 
                Submitting a report will place the volunteer's clearance under review.
                All reports are confidential and reviewed by administrators.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Volunteer Email *</label>
                <input
                  type="email"
                  className="input-field"
                  value={formData.volunteer_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, volunteer_id: e.target.value }))}
                  placeholder="volunteer@example.com"
                  required
                  data-testid="incident-volunteer-email"
                />
                <p className="text-xs text-[#666666] mt-1">
                  Enter the email address of the volunteer involved in the incident
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Incident Type *</label>
                <select
                  className="input-field"
                  value={formData.incident_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_type: e.target.value }))}
                  required
                  data-testid="incident-type-select"
                >
                  {INCIDENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Date of Incident *</label>
                <input
                  type="date"
                  className="input-field"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  data-testid="incident-date-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <textarea
                  className="input-field min-h-[150px]"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Provide a detailed description of the incident. Include relevant facts, dates, times, and any witnesses if applicable."
                  required
                  data-testid="incident-description-input"
                />
                <p className="text-xs text-[#666666] mt-1">
                  Be factual and specific. Do not include personal opinions or assumptions.
                </p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E9DDCF]">
              <p className="text-xs text-[#666666] mb-4">
                By submitting this report, you confirm that the information provided is accurate to the best of your knowledge.
                False reports may result in consequences for your organization.
              </p>
              
              <div className="flex gap-3 justify-end">
                <Link to="/partner/dashboard" className="btn-outline">
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center gap-2"
                  data-testid="submit-incident-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default IncidentReportForm;
